import React, { useState, useEffect, useCallback } from 'react';
import { CircularProgress, Box, Chip } from '@mui/material';
import CompanyCard, { STATUS_CONFIG } from './CompanyCard';
import { useCompany } from '../contexts/CompanyContext';
import { getCompanyStatusesBatch, getCompanyIdentifier } from '../services/api';

const CompanyList = () => {
  const { companies, searchCity, searchMetadata } = useCompany();
  const [companyStatuses, setCompanyStatuses] = useState({});
  const [loadingStatuses, setLoadingStatuses] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');

  // Загрузка статусов для всех компаний (по inn или internal_id)
  const loadCompanyStatuses = useCallback(async () => {
    if (!companies || companies.length === 0) return;

    // Filter companies that have an identifier (inn or internal_id)
    const companiesWithIds = companies.filter(c => getCompanyIdentifier(c));

    if (companiesWithIds.length === 0) return;

    setLoadingStatuses(true);
    try {
      const statuses = await getCompanyStatusesBatch(companiesWithIds);
      setCompanyStatuses(statuses);
    } catch (error) {
      console.error('CompanyList: Error loading statuses:', error);
    } finally {
      setLoadingStatuses(false);
    }
  }, [companies]);

  useEffect(() => {
    loadCompanyStatuses();
  }, [loadCompanyStatuses]);

  // Слушаем событие изменения статуса
  useEffect(() => {
    const handleExportStatusChanged = (event) => {
      const { companyIdentifier, companyInn, companyInternalId, status, comment } = event.detail || {};
      
      // Use companyIdentifier as key (can be inn or internal_id)
      const key = companyIdentifier || companyInn || companyInternalId;
      
      if (key) {
        // Если status === null — удаляем статус (компания становится "новой")
        if (status === null) {
          setCompanyStatuses(prev => {
            const newStatuses = { ...prev };
            delete newStatuses[key];
            return newStatuses;
          });
        } else {
          setCompanyStatuses(prev => ({
            ...prev,
            [key]: {
              inn: companyInn,
              internal_id: companyInternalId,
              status: status,
              comment: comment || '',
              updated_at: new Date().toISOString()
            }
          }));
        }
      }
    };

    window.addEventListener('exportStatusChanged', handleExportStatusChanged);
    return () => window.removeEventListener('exportStatusChanged', handleExportStatusChanged);
  }, []);

  if (!companies || companies.length === 0) {
    return (
      <div className="container">
        <h1>Нет данных для отображения</h1>
        <p>Пожалуйста, выполните поиск компаний сначала.</p>
      </div>
    );
  }

  // Helper to get status for a company
  const getStatusForCompany = (company) => {
    const id = getCompanyIdentifier(company);
    return id ? companyStatuses[id.value] : null;
  };

  // Подсчёт статистики
  const statusCounts = companies.reduce((acc, company) => {
    const companyStatusObj = getStatusForCompany(company);
    const status = companyStatusObj?.status;
    if (status) {
      acc[status] = (acc[status] || 0) + 1;
    } else {
      acc.no_status = (acc.no_status || 0) + 1;
    }
    return acc;
  }, { no_status: 0 });

  // Фильтрация компаний
  const filteredCompanies = companies.filter(company => {
    if (statusFilter === 'all') return true;
    const companyStatusObj = getStatusForCompany(company);
    if (statusFilter === 'no_status') {
      return !companyStatusObj;
    }
    return companyStatusObj?.status === statusFilter;
  });

  const toggleFilter = (filter) => {
    setStatusFilter(prev => prev === filter ? 'all' : filter);
  };

  return (
    <div className="container">
      <h1>Компании {searchCity ? `— ${searchCity}` : ''}</h1>

      {/* Фильтры-чипы */}
      <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <Chip
          label={`Все ${companies.length}`}
          onClick={() => setStatusFilter('all')}
          variant={statusFilter === 'all' ? 'filled' : 'outlined'}
          color={statusFilter === 'all' ? 'primary' : 'default'}
          size="small"
        />
        
        {statusCounts.no_status > 0 && (
          <Chip
            label={`🆕 Новые ${statusCounts.no_status}`}
            onClick={() => toggleFilter('no_status')}
            variant={statusFilter === 'no_status' ? 'filled' : 'outlined'}
            size="small"
            sx={{
              bgcolor: statusFilter === 'no_status' ? '#1976d2' : 'transparent',
              color: statusFilter === 'no_status' ? 'white' : 'inherit',
              borderColor: '#1976d2'
            }}
          />
        )}

        {Object.entries(STATUS_CONFIG).map(([key, config]) => {
          const count = statusCounts[key] || 0;
          if (count === 0) return null;
          
          const isActive = statusFilter === key;
          return (
            <Chip
              key={key}
              label={`${config.icon} ${config.label} ${count}`}
              onClick={() => toggleFilter(key)}
              variant={isActive ? 'filled' : 'outlined'}
              size="small"
              sx={{
                bgcolor: isActive ? config.color : 'transparent',
                color: isActive ? 'white' : config.color,
                borderColor: config.color,
                '&:hover': {
                  bgcolor: isActive ? config.color : config.bgColor
                }
              }}
            />
          );
        })}

        {loadingStatuses && <CircularProgress size={18} sx={{ ml: 1 }} />}
      </Box>

      <div className="companies-grid">
        {filteredCompanies
          .map((company) => ({
            company,
            originalIndex: companies.indexOf(company)
          }))
          .sort((a, b) => {
            const scoreA = a.company.analysis?.score || a.company.gemini_analysis?.score || 0;
            const scoreB = b.company.analysis?.score || b.company.gemini_analysis?.score || 0;
            return scoreB - scoreA;
          })
          .map(({ company, originalIndex }) => {
            const companyId = getCompanyIdentifier(company);
            return (
              <CompanyCard
                key={`${companyId?.value || company.id || originalIndex}`}
                company={company}
                index={originalIndex}
                searchMetadata={searchMetadata}
                companyStatus={companyId ? companyStatuses[companyId.value] : null}
                onStatusChange={(identifier, newStatus) => {
                  if (newStatus === null) {
                    setCompanyStatuses(prev => {
                      const updated = { ...prev };
                      delete updated[identifier];
                      return updated;
                    });
                  } else {
                    setCompanyStatuses(prev => ({
                      ...prev,
                      [identifier]: newStatus
                    }));
                  }
                }}
              />
            );
          })}
      </div>

      <div className="summary">
        <p>Всего компаний: {filteredCompanies.length}{statusFilter !== 'all' ? ` из ${companies.length}` : ''}</p>
      </div>
    </div>
  );
};

export default CompanyList;
